import React, { useState, useEffect, useCallback } from "react";
import {
  adminAffiliatesService,
  SearchAffiliate,
  AdminAffiliate,
  AffiliateSearchParams,
  SearchAffiliatesResponse,
  AffiliateEnrollment,
} from "../../services/admin/affiliates.admin.service";
import { DataTable, DataTablePagination } from "@/components/ui/data-table";
import type {
  ColumnDef,
  OnChangeFn,
  SortingState,
} from "@tanstack/react-table";
import { AdminFilters } from "../../components/admin/AdminFilters";
import {
  Users,
  RefreshCw,
  Mail,
  Calendar,
  DollarSign,
  UserCheck,
  Phone,
  Globe,
  Shield,
  ShieldCheck,
  Tag,
  Clock,
  Trash2,
} from "lucide-react";
import { Facebook, Instagram, Youtube, Twitter } from "react-feather";
import { DiscordIcon } from "../../assets/discord";
import TikTokIcon from "../../assets/tiktok.tsx";
import { formatSocialMediaUrl } from "../../utils/socialMediaUtils";
import { format } from "date-fns";
import { BrandMinimal } from "@/types/brand";
import { adminBrandsService } from "@/services/admin/brands.admin.service";
import { formatstorageUrl } from "@/utils/formatters.ts";
import { ConfirmationModal } from "@/components/ui/confirmation-modal";
import toast from "react-hot-toast";

// Custom debounce hook for search and simple filters
function useDebounce(callback: () => void, delay: number) {
  const debounceRef = React.useRef<NodeJS.Timeout>();

  return React.useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => callback(), delay);
  }, [callback, delay]);
}

// Skeleton Components
function StatCardSkeleton() {
  return (
    <div className="bg-transparent border border-border rounded-xl animate-pulse p-6">
      <div className="flex items-center justify-between space-x-4">
        <div className="space-y-2 flex-1">
          <div className="h-3 bg-muted rounded w-1/2"></div>
          <div className="h-8 bg-muted rounded w-1/3"></div>
        </div>
        <div className="h-12 w-12 rounded-2xl border border-border bg-transparent"></div>
      </div>
    </div>
  );
}

// Stats Card Component
interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  formatter?: (value: number) => string;
}

function StatCard({ title, value, icon, color, formatter }: StatCardProps) {
  const displayValue =
    typeof value === "number" && formatter ? formatter(value) : value;
  const isWarm = color === "bg-orange-100 dark:bg-orange-500/15";

  return (
    <div className="bg-transparent border border-border rounded-xl transition-all duration-300 hover:border-[#d7785b]/40">
      <div className="p-6">
        <div className="flex items-center justify-between space-x-4">
          <div className="space-y-2 flex-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {title}
            </p>
            <p className="text-3xl font-bold text-foreground">{displayValue}</p>
          </div>
          <div
            className={`h-12 w-12 rounded-2xl flex items-center justify-center border ${
              isWarm
                ? "border-[#d7785b]/20 bg-transparent"
                : "border-border bg-transparent"
            }`}
          >
            <div className={isWarm ? "text-[#d7785b]" : "text-muted-foreground"}>
              {icon}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Union type for affiliate data
type AffiliateData = SearchAffiliate | AdminAffiliate;

export default function AdminAffiliates() {
  const [affiliates, setAffiliates] = useState<AffiliateData[]>([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<AffiliateSearchParams["sortBy"]>("JOIN_DATE");
  const [sortOrder, setSortOrder] =
    useState<AffiliateSearchParams["sortOrder"]>("DESC");
  const [earningsRange, setEarningsRange] = useState<[number, number]>([
    0, 10000,
  ]);
  const [joinedAfter, setJoinedAfter] = useState("");
  const [selectedAffiliate, setSelectedAffiliate] =
    useState<AffiliateData | null>(null);
  const [detailedAffiliate, setDetailedAffiliate] =
    useState<AdminAffiliate | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [removeEnrollmentTarget, setRemoveEnrollmentTarget] = useState<{
    affiliateId: string;
    enrollment: AffiliateEnrollment;
  } | null>(null);
  const [removingEnrollment, setRemovingEnrollment] = useState(false);

  // Pagination for search results
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Track if we're using search mode
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<string[]>([]);
  const [brandList, setBrandList] = useState<BrandMinimal[]>([]);

  // Stats
  const [stats, setStats] = useState({
    totalAffiliates: 0,
    activeEarners: 0,
    totalEarnings: 0,
    averageEarnings: 0,
  });

  // Check if we need to use search mode
  const needsSearchMode = Boolean(
    searchQuery ||
      earningsRange[0] > 0 ||
      earningsRange[1] < 10000 ||
      joinedAfter ||
      sortBy !== "JOIN_DATE" ||
      sortOrder !== "DESC"
  );

  // Fetch all affiliates using the search API with minimal params
  const fetchAllAffiliates = useCallback(async () => {
    setLoading(true);
    try {
      // Use search API with minimal parameters to get all affiliates
      const params: AffiliateSearchParams = {
        page,
        limit: pageSize,
        sortBy,
        sortOrder,
        programIds: selectedBrand,
      };

      const response: SearchAffiliatesResponse =
        await adminAffiliatesService.searchAffiliates(params);

      setAffiliates(response.data);
      setTotal(response.pagination.total);
      setTotalPages(response.pagination.totalPages);
      setIsSearchMode(false);
    } catch (error) {
      console.error("Failed to fetch affiliates:", error);
      setAffiliates([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortBy, sortOrder, selectedBrand]);

  useEffect(() => {
    const fetchBrandList = async () => {
      const brandList = await adminBrandsService.getFilteringBrands();
      setBrandList(brandList);
    };
    fetchBrandList();
  }, []);

  // Search affiliates (search mode)
  const searchAffiliates = useCallback(async () => {
    setLoading(true);
    try {
      const params: AffiliateSearchParams = {
        page,
        limit: pageSize,
        sortBy,
        sortOrder,
      };

      if (searchQuery) {
        params.keyword = searchQuery;
        params.searchBy = "NAME";
      }

      if (earningsRange[0] > 0 || earningsRange[1] < 10000) {
        params.minEarnings = earningsRange[0];
        params.maxEarnings = earningsRange[1];
      }

      if (joinedAfter) {
        params.joinedAfter = joinedAfter;
      }

      if (selectedBrand) {
        params.programIds = selectedBrand;
      }

      const response: SearchAffiliatesResponse =
        await adminAffiliatesService.searchAffiliates(params);
      setAffiliates(response.data);
      setTotal(response.pagination.total);
      setTotalPages(response.pagination.totalPages);
      setIsSearchMode(true);
    } catch (error) {
      console.error("Failed to search affiliates:", error);
      setAffiliates([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortBy, sortOrder, searchQuery, earningsRange, joinedAfter, selectedBrand]);

  // Main fetch function that decides which search parameters to use
  const fetchData = useCallback(() => {
    if (needsSearchMode) {
      // Use search with specific filters
      searchAffiliates();
    } else {
      // Use search with minimal parameters
      fetchAllAffiliates();
    }
  }, [needsSearchMode, searchAffiliates, fetchAllAffiliates]);

  // Effect to update search mode when filter criteria change
  useEffect(() => {
    setIsSearchMode(needsSearchMode);
    // Don't trigger search automatically here
  }, [needsSearchMode]);

  // Debounced search for simple filters (search input, dropdown filters)
  const debouncedSimpleSearch = useDebounce(() => {
    setPage(1);
    if (needsSearchMode) {
      searchAffiliates();
    } else {
      fetchAllAffiliates();
    }
  }, 1000);

  // Filter handlers
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    // Search input - auto-trigger with debounce
    if (value.trim()) {
      debouncedSimpleSearch();
    }
  };

  const handleEarningsRangeChange = (newRange: [number, number]) => {
    setEarningsRange(newRange);
    // Advanced filter - only update state, don't auto-trigger
  };

  const handleJoinedAfterChange = (date: string) => {
    setJoinedAfter(date);
    // Advanced filter - only update state, don't auto-trigger
  };

  const handleSortByChange = (value: string | string[]) => {
    const newSortBy = Array.isArray(value) ? value[0] : value;
    setSortBy(newSortBy as AffiliateSearchParams["sortBy"]);
    // Simple filter - auto-trigger with debounce
    debouncedSimpleSearch();
  };

  const handleSortOrderChange = (value: string | string[]) => {
    const newSortOrder = Array.isArray(value) ? value[0] : value;
    setSortOrder(newSortOrder as AffiliateSearchParams["sortOrder"]);
    // Simple filter - auto-trigger with debounce
    debouncedSimpleSearch();
  };

  const handleBrandChange = (value: string | string[]) => {
    const newBrand = Array.isArray(value) ? value : [value];
    setSelectedBrand(newBrand);
    // Simple filter - auto-trigger with debounce
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setEarningsRange([0, 10000]);
    setJoinedAfter("");
    setSortBy("JOIN_DATE");
    setSortOrder("DESC");
    setPage(1);
    setSelectedBrand([]);
    fetchData();
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchData();
  };

  // Fetch stats
  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const statsData = await adminAffiliatesService.getAffiliateStats();
      setStats(statsData);
    } catch (error) {
      console.error("Failed to fetch affiliate stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Effect to fetch data when page changes
  useEffect(() => {
    fetchData();
  }, [page, fetchData]);

  // Initial load
  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle sorting change
  const handleSortChange = (field: string) => {
    // Map table column keys to backend sort values
    const fieldMapping: Record<string, AffiliateSearchParams["sortBy"]> = {
      "name": "NAME",
      "joinedAt": "JOIN_DATE", 
      "earnings": "EARNINGS"
    };
    
    const newSortBy = fieldMapping[field];
    if (!newSortBy) return;
    
    if (newSortBy === sortBy) {
      setSortOrder(sortOrder === "DESC" ? "ASC" : "DESC");
    } else {
      setSortBy(newSortBy);
      setSortOrder("DESC");
    }
    setPage(1);
  };

  // TanStack sorting state derived from the server-side sortBy/sortOrder
  const sortingIdMap: Record<string, string> = {
    NAME: "name",
    JOIN_DATE: "joinedAt",
    EARNINGS: "earnings",
  };
  const tableSorting: SortingState = sortBy
    ? [{ id: sortingIdMap[sortBy], desc: sortOrder === "DESC" }]
    : [];

  const handleTableSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === "function" ? updater(tableSorting) : updater;
    const clickedId = next[0]?.id ?? tableSorting[0]?.id;
    if (clickedId) handleSortChange(clickedId);
  };

  // Handle pagination change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Handle view affiliate details
  const handleViewAffiliate = async (affiliate: AffiliateData) => {
    setSelectedAffiliate(affiliate);
    setShowModal(true);
    setModalLoading(true);

    try {
      // Fetch detailed affiliate information
      const detailedData = await adminAffiliatesService.getAffiliateById(
        affiliate.id
      );
      setDetailedAffiliate(detailedData);
    } catch (error) {
      console.error("Failed to fetch detailed affiliate data:", error);
      setDetailedAffiliate(null);
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAffiliate(null);
    setDetailedAffiliate(null);
  };

  // Format currency
  const formatCurrency = (value: number) =>
    `$${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // Check if affiliate is SearchAffiliate type
  const isSearchAffiliate = (
    affiliate: AffiliateData
  ): affiliate is SearchAffiliate => {
    return "totalEarnings" in affiliate;
  };

  const handleConfirmRemoveEnrollment = async () => {
    if (!removeEnrollmentTarget) return;
    const { affiliateId, enrollment } = removeEnrollmentTarget;
    setRemovingEnrollment(true);
    try {
      await adminAffiliatesService.removeEnrollment(
        enrollment.programId,
        affiliateId,
      );
      setSelectedAffiliate((prev) => {
        if (!prev || !isSearchAffiliate(prev) || prev.id !== affiliateId) {
          return prev;
        }
        return {
          ...prev,
          enrollments: prev.enrollments.filter(
            (e) => e.programId !== enrollment.programId,
          ),
        };
      });
      setAffiliates((prev) =>
        prev.map((a) => {
          if (!isSearchAffiliate(a) || a.id !== affiliateId) return a;
          return {
            ...a,
            enrollments: a.enrollments.filter(
              (e) => e.programId !== enrollment.programId,
            ),
          };
        }),
      );
      setRemoveEnrollmentTarget(null);
      toast.success("Affiliate removed from program");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to remove affiliate from program",
      );
    } finally {
      setRemovingEnrollment(false);
    }
  };

  // Table columns
  const columns: ColumnDef<AffiliateData>[] = [
    {
      id: "name",
      header: "Affiliate",
      enableSorting: true,
      meta: { width: "w-52" },
      cell: ({ row }) => {
        const affiliate = row.original;
        return (
          <div className="flex items-center gap-3 min-w-0">
            {affiliate.profileImage ? (
              <img
                src={formatstorageUrl(affiliate.profileImage)}
                alt=""
                className="w-8 h-8 rounded-full object-cover shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-[#d7785b]/10 flex items-center justify-center shrink-0">
                <Users className="w-4 h-4 text-[#d7785b]" />
              </div>
            )}
            <div className="font-medium text-foreground truncate">
              {affiliate.firstName} {affiliate.lastName}
            </div>
          </div>
        );
      },
    },
    {
      id: "username",
      header: "Username",
      meta: { width: "w-36" },
      cell: ({ row }) => (
        <div className="text-sm text-muted-foreground truncate">
          {row.original.username || "No username"}
        </div>
      ),
    },
    {
      id: "email",
      header: "Contact",
      meta: { width: "w-56" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-foreground min-w-0">
          <Mail className="w-3 h-3 text-muted-foreground shrink-0" />
          <span className="truncate">{row.original.email}</span>
        </div>
      ),
    },
    {
      id: "socials",
      header: "Socials",
      meta: { width: "w-32" },
      cell: ({ row }) => {
        const affiliate = row.original;
        const renderSocialIcon = (platform: string) => {
          switch (platform.toUpperCase()) {
            case "FACEBOOK":
              return <Facebook className="w-4 h-4" />;
            case "INSTAGRAM":
              return <Instagram className="w-4 h-4" />;
            case "TIKTOK":
              return <TikTokIcon className="w-4 h-4" />;
            case "YOUTUBE":
              return <Youtube className="w-4 h-4" />;
            case "DISCORD":
              return <DiscordIcon className="w-4 h-4" />;
            case "TWITTER":
            case "X":
              return <Twitter className="w-4 h-4" />;
            default:
              return null;
          }
        };

        return (
          <div className="flex items-center gap-2">
            {affiliate.socialLinks.map((social) => {
              if (!social.platform || !social.username) return null;
              
              const url =
                formatSocialMediaUrl(social.username, social.platform) || social.url;
              
              const icon = renderSocialIcon(social.platform);
              
              if (!icon) return null;
              
              return (
                <a
                  key={social.id}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-muted-foreground transition-colors"
                  title={`${social.platform}: ${social.username}`}
                >
                  {icon}
                </a>
              );
            })}
            {affiliate.socialLinks.length === 0 && (
              <div className="text-muted-foreground text-sm">No socials</div>
            )}
          </div>
        );
      },
    },
    {
      id: "joinedAt",
      header: "Joined",
      enableSorting: true,
      meta: { fit: true },
      cell: ({ row }) => {
        const affiliate = row.original;
        const joinedAt = isSearchAffiliate(affiliate)
          ? affiliate.joinedAt
          : new Date().toISOString(); // AdminAffiliate doesn't have joinedAt

        return (
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Calendar className="w-3 h-3 text-muted-foreground" />
            {format(new Date(joinedAt), "MMM dd, yyyy")}
          </div>
        );
      },
    },
    {
      id: "earnings",
      header: "Earnings",
      enableSorting: true,
      meta: { width: "w-32" },
      cell: ({ row }) => {
        const affiliate = row.original;
        if (isSearchAffiliate(affiliate)) {
          return (
            <div className="flex items-center gap-1">
              <DollarSign className="w-3 h-3 text-muted-foreground" />
              <span
                className={`font-medium ${
                  affiliate.totalEarnings > 0
                    ? "text-green-600 dark:text-green-400"
                    : "text-muted-foreground"
                }`}
              >
                {formatCurrency(affiliate.totalEarnings)}
              </span>
            </div>
          );
        } else {
          return (
            <div className="flex items-center gap-1">
              <DollarSign className="w-3 h-3 text-muted-foreground" />
              <span className="text-muted-foreground text-sm">N/A</span>
            </div>
          );
        }
      },
    },
    {
      id: "programs",
      header: "Programs",
      meta: { width: "w-36" },
      cell: ({ row }) => {
        const affiliate = row.original;
        if (isSearchAffiliate(affiliate)) {
          const activeEnrollments = affiliate.enrollments.filter(
            (e) => e.status === "APPROVED"
          ).length;
          return (
            <div className="text-sm whitespace-nowrap">
              <span className="font-medium">{activeEnrollments} active</span>
              <span className="text-muted-foreground">
                {" "}· {affiliate.enrollments.length} total
              </span>
            </div>
          );
        } else {
          return <div className="text-sm text-muted-foreground">N/A</div>;
        }
      },
    },
    {
      id: "profileStatus",
      header: "Profile Status",
      meta: { width: "w-36" },
      cell: ({ row }) => {
        const isCompleted = row.original.isProfileCompleted ?? true;
        
        return (
          <div className="flex items-center gap-2">
            {isCompleted ? (
              <>
                <UserCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                  Completed
                </span>
              </>
            ) : (
              <>
                <Clock className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
                  Incomplete
                </span>
              </>
            )}
          </div>
        );
      },
    },
  ];

  // Always use server-side pagination - return the data as-is from API
  const getPaginatedData = () => {
    return affiliates;
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-semibold text-foreground mb-2">
            Affiliate Management
          </h1>
          <p className="text-muted-foreground">
            Manage and track all affiliates on your platform.
          </p>
        </div>
        <button
          onClick={() => {
            fetchData();
            fetchStats();
          }}
          className="flex items-center gap-2 px-6 py-3 text-white rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
          style={{ backgroundColor: "#d7785b" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#c66a50";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#d7785b";
          }}
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mb-8">
        {statsLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <StatCardSkeleton key={index} />
          ))
        ) : (
          <>
            <StatCard
              title="Total Affiliates"
              value={stats.totalAffiliates}
              icon={<Users className="w-5 h-5 text-foreground" />}
              color="bg-muted"
            />
            <StatCard
              title="Active Earners"
              value={stats.activeEarners}
              icon={
                <UserCheck className="w-5 h-5" style={{ color: "#d7785b" }} />
              }
              color="bg-orange-100 dark:bg-orange-500/15"
            />
            <StatCard
              title="Total Earnings"
              value={stats.totalEarnings}
              icon={<DollarSign className="w-5 h-5 text-foreground" />}
              color="bg-muted"
              formatter={formatCurrency}
            />
            <StatCard
              title="Avg. Earnings"
              value={stats.averageEarnings}
              icon={
                <DollarSign className="w-5 h-5" style={{ color: "#d7785b" }} />
              }
              color="bg-orange-100 dark:bg-orange-500/15"
              formatter={formatCurrency}
            />
          </>
        )}
      </div>

      {/* Enhanced Filters using the AdminFilters component */}
      <div className="">
        <AdminFilters
          filters={[
            {
              label: "Sort By",
              value: sortBy || "",
              onChange: handleSortByChange,
              showLabel: true,
              options: [
                { label: "Join Date", value: "JOIN_DATE" },
                { label: "Name", value: "NAME" },
                { label: "Earnings", value: "EARNINGS" },
              ],
            },
            {
              label: "Sort Order",
              value: sortOrder || "",
              onChange: handleSortOrderChange,
              showLabel: true,
              options: [
                { label: "Descending", value: "DESC" },
                { label: "Ascending", value: "ASC" },
              ],
            },
            {
              label: "Brand",
              value: selectedBrand[0] || "",
              onChange: handleBrandChange,
              isMulti: false,
              options: brandList.map((brand) => ({ label: brand.name, value: brand.programId })),
            },
          ]}
          searchPlaceholder="Search affiliates by name..."
          onSearch={handleSearch}
          searchValue={searchQuery}
          revenueRange={{
            min: 0,
            max: 10000,
            value: earningsRange,
            onChange: handleEarningsRangeChange,
            label: "Earnings",
          }}
          dateFilter={{
            value: joinedAfter,
            onChange: handleJoinedAfterChange,
            label: "Joined After",
            placeholder: "Select date",
          }}
          onReset={handleResetFilters}
          onApply={handleApplyFilters}
        />

        {isSearchMode && (
          <div className="mt-4 flex items-center gap-2">
            <div
              className="text-sm font-medium px-4 py-2 rounded-xl border-2"
              style={{
                color: "#d7785b",
                backgroundColor: "rgba(215, 120, 91, 0.1)",
                borderColor: "rgba(215, 120, 91, 0.3)",
              }}
            >
              Search Mode Active
            </div>
          </div>
        )}
      </div>

      {/* Affiliates Table */}
      <div className="bg-card rounded-xl border border-border shadow-sm">
        <div className="p-8 border-b border-border">
          <h3 className="text-xl font-semibold text-foreground">
            Affiliates ({total.toLocaleString()})
            {isSearchMode && (
              <span className="text-sm text-muted-foreground ml-2">
                (Search Results)
              </span>
            )}
          </h3>
        </div>

        <DataTable
          data={getPaginatedData()}
          columns={columns}
          isLoading={loading}
          emptyText={
            searchQuery
              ? "No affiliates found matching your search criteria"
              : "No affiliates have joined yet"
          }
          onRowClick={handleViewAffiliate}
          sorting={tableSorting}
          onSortingChange={handleTableSortingChange}
          manualSorting
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <DataTablePagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
            onPageChange={handlePageChange}
            noun="affiliates"
          />
        )}
      </div>

      {/* Enhanced Affiliate Detail Modal */}
      {showModal && selectedAffiliate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground">
                Affiliate Details
              </h2>
              <button
                onClick={closeModal}
                className="text-muted-foreground hover:text-muted-foreground"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {modalLoading ? (
              <div className="flex items-center justify-center py-12">
                <div
                  className="animate-spin rounded-full h-12 w-12 border-b-2"
                  style={{ borderColor: "#d7785b" }}
                ></div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Header Section */}
                <div className="bg-gradient-to-r from-muted to-orange-100/60 dark:to-orange-500/10 rounded-xl p-6">
                  <div className="flex items-center gap-6">
                    {selectedAffiliate.profileImage ? (
                      <img
                        src={ formatstorageUrl(selectedAffiliate.profileImage) }
                        alt=""
                        className="w-20 h-20 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-orange-100 dark:bg-orange-500/15 flex items-center justify-center">
                        <Users
                          className="w-10 h-10"
                          style={{ color: "#d7785b" }}
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-foreground">
                        {selectedAffiliate.firstName}{" "}
                        {selectedAffiliate.lastName}
                      </h3>
                      <p className="text-muted-foreground text-lg">
                        {selectedAffiliate.username || "No username"}
                      </p>
                      <div className="flex items-center gap-4 mt-2">
                        <div className="flex items-center gap-1">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            {selectedAffiliate.email}
                          </span>
                        </div>
                        {detailedAffiliate && (
                          <>
                            <div
                              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                                detailedAffiliate.isActive
                                  ? "bg-green-100 dark:bg-green-500/15 text-green-800 dark:text-green-300"
                                  : "bg-red-100 dark:bg-red-500/15 text-red-800 dark:text-red-300"
                              }`}
                            >
                              <Shield className="w-3 h-3" />
                              {detailedAffiliate.isActive
                                ? "Active"
                                : "Inactive"}
                            </div>
                            <div
                              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                                detailedAffiliate.isEmailVerified
                                  ? "bg-blue-100 dark:bg-blue-500/15 text-blue-800 dark:text-blue-300"
                                  : "bg-yellow-100 dark:bg-yellow-500/15 text-yellow-800 dark:text-yellow-300"
                              }`}
                            >
                              <ShieldCheck className="w-3 h-3" />
                              {detailedAffiliate.isEmailVerified
                                ? "Email Verified"
                                : "Email Unverified"}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Personal Information */}
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-foreground mb-4">
                      Personal Information
                    </h4>
                    {detailedAffiliate ? (
                      <div className="space-y-3">
                        {detailedAffiliate.country && (
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              Country:
                            </span>
                            <span className="text-sm font-medium">
                              {detailedAffiliate.country}
                            </span>
                          </div>
                        )}

                        {detailedAffiliate.phoneNumber?.fullNumber && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              Phone:
                            </span>
                            <span className="text-sm font-medium">
                              {detailedAffiliate.phoneNumber.fullNumber}
                            </span>
                          </div>
                        )}

                        {detailedAffiliate.gender && (
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              Gender:
                            </span>
                            <span className="text-sm font-medium capitalize">
                              {detailedAffiliate.gender.toLowerCase()}
                            </span>
                          </div>
                        )}

                        {detailedAffiliate.age && (
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Age:</span>
                            <span className="text-sm font-medium">
                              {detailedAffiliate.age} years
                            </span>
                          </div>
                        )}

                        {detailedAffiliate.timezone && (
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              Timezone:
                            </span>
                            <span className="text-sm font-medium">
                              {detailedAffiliate.timezone}
                            </span>
                          </div>
                        )}

                        {detailedAffiliate.bio && (
                          <div className="mt-4">
                            <span className="text-sm text-muted-foreground">Bio:</span>
                            <p className="text-sm text-foreground mt-1 bg-muted p-3 rounded-lg">
                              {detailedAffiliate.bio}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        Loading personal information...
                      </div>
                    )}
                  </div>

                  {/* Performance Information */}
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-foreground mb-4">
                      Performance Overview
                    </h4>
                    {isSearchAffiliate(selectedAffiliate) ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-green-50 dark:bg-green-500/10 p-4 rounded-lg text-center">
                            <div className="text-sm text-green-600 dark:text-green-400 font-medium">
                              Total Earnings
                            </div>
                            <div className="text-xl font-bold text-green-900">
                              {formatCurrency(selectedAffiliate.totalEarnings)}
                            </div>
                          </div>
                          <div className="bg-blue-50 dark:bg-blue-500/10 p-4 rounded-lg text-center">
                            <div className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                              Programs
                            </div>
                            <div className="text-xl font-bold text-blue-900">
                              {selectedAffiliate.enrollments.length}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Joined:</span>
                          <span className="text-sm font-medium">
                            {format(
                              new Date(selectedAffiliate.joinedAt),
                              "MMM dd, yyyy"
                            )}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        Performance data not available
                      </div>
                    )}
                  </div>
                </div>

                {/* Niches Section */}
                {detailedAffiliate && detailedAffiliate.niches.length > 0 && (
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-foreground mb-4">
                      Interest Niches
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {detailedAffiliate.niches.map((niche, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-1 px-3 py-2 rounded-full text-sm"
                          style={{
                            backgroundColor: "rgba(215, 120, 91, 0.1)",
                            color: "#8b5a3c",
                          }}
                        >
                          <Tag className="w-3 h-3" />
                          {niche.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Program Enrollments */}
                {isSearchAffiliate(selectedAffiliate) &&
                  selectedAffiliate.enrollments.filter(
                    (e) => (e.status as string) !== "INACTIVE",
                  ).length > 0 && (
                    <div className="bg-card border border-border rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-foreground mb-4">
                        Program Enrollments
                      </h4>
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {selectedAffiliate.enrollments
                          .filter((e) => (e.status as string) !== "INACTIVE")
                          .map((enrollment, index) => (
                            <div
                              key={`${enrollment.programId}-${index}`}
                              className="flex items-center justify-between p-4 bg-muted rounded-lg hover:bg-accent transition-colors gap-3"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-foreground">
                                  {enrollment.brandName}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  Code: {enrollment.code}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  Joined:{" "}
                                  {format(
                                    new Date(enrollment.joinedAt),
                                    "MMM dd, yyyy"
                                  )}
                                </div>
                              </div>
                              <div className="text-right space-y-1 shrink-0">
                                <div
                                  className={`text-xs px-2 py-1 rounded-full font-medium inline-block ${
                                    enrollment.status === "APPROVED"
                                      ? "bg-green-100 dark:bg-green-500/15 text-green-800 dark:text-green-300"
                                      : enrollment.status === "PENDING"
                                      ? "bg-yellow-100 dark:bg-yellow-500/15 text-yellow-800 dark:text-yellow-300"
                                      : "bg-red-100 dark:bg-red-500/15 text-red-800 dark:text-red-300"
                                  }`}
                                >
                                  {enrollment.status}
                                </div>
                                <div className="text-sm font-medium text-foreground">
                                  {formatCurrency(enrollment.totalCommissions)}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {enrollment.totalOrders} orders
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  setRemoveEnrollmentTarget({
                                    affiliateId: selectedAffiliate.id,
                                    enrollment,
                                  })
                                }
                                className="p-1.5 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors shrink-0"
                                title="Remove from program"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={!!removeEnrollmentTarget}
        onClose={() => {
          if (!removingEnrollment) setRemoveEnrollmentTarget(null);
        }}
        onConfirm={handleConfirmRemoveEnrollment}
        title="Remove from Program"
        message={
          removeEnrollmentTarget
            ? `Remove this affiliate from "${removeEnrollmentTarget.enrollment.brandName}"? This only removes their enrollment in this program — their account and any other programs they're in stay untouched. The discount code and tracking link they use for this program will stop working. Past sales and payout records are kept, and they can rejoin later, though their stats for this program will start fresh.`
            : ""
        }
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        isLoading={removingEnrollment}
      />
    </div>
  );
}
